/**
 * SafetyDashboard — compact, read-only fleet-safety KPI strip shown atop the
 * Incident Register. Derived entirely from existing SOS events + audit data.
 * Gives operators, compliance managers, councils and insurers an at-a-glance
 * read of safety performance without opening individual incidents.
 */
import React, { useState, useEffect } from "react";
import {
  ShieldAlert, AlertTriangle, Siren, CheckCircle2, Timer, Gauge,
  Activity, Repeat, ArrowUp, ArrowDown, Minus, Clock3, Loader2,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const fmtDuration = (secs) => {
  if (secs == null) return "—";
  const s = Math.round(secs);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60), rem = s % 60;
  if (m < 60) return rem ? `${m}m ${rem}s` : `${m}m`;
  const h = Math.floor(m / 60), mm = m % 60;
  return mm ? `${h}h ${mm}m` : `${h}h`;
};

const Trend = ({ value, prev, goodWhenDown, isDuration }) => {
  if (prev == null || value == null) return null;
  const delta = value - prev;
  if (Math.abs(delta) < (isDuration ? 1 : 0.0001)) {
    return <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground"><Minus className="w-3 h-3" /> flat</span>;
  }
  const up = delta > 0;
  const good = goodWhenDown ? !up : up;
  const cls = good ? "text-[#059669]" : "text-[#DC2626]";
  const label = isDuration ? fmtDuration(Math.abs(delta)) : Math.abs(delta);
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${cls}`} data-testid="kpi-trend">
      {up ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />} {label} vs last mo.
    </span>
  );
};

const Card = ({ icon: Icon, label, display, accent, k, isDuration, goodWhenDown, onClick }) => (
  <button type="button" onClick={onClick} data-testid={`kpi-${k.testid}`}
    className="text-left border border-border rounded-xl p-3 bg-white flex flex-col gap-1 transition-colors hover:border-[#0A0A0A] hover:bg-secondary/40 focus:outline-none focus:ring-2 focus:ring-[#0A0A0A]/20">
    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
      <Icon className={`w-3.5 h-3.5 ${accent}`} /> {label}
    </div>
    <div className="text-2xl font-bold leading-none" data-testid={`kpi-${k.testid}-value`}>{display}</div>
    <Trend value={k.value} prev={k.prev} goodWhenDown={goodWhenDown} isDuration={isDuration} />
  </button>
);

export const SafetyDashboard = ({ onDrill }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let on = true;
    api.get("/admin/sos/kpis")
      .then(({ data }) => { if (on) setData(data); })
      .catch((e) => { if (on) setErr(formatApiError(e) || "Failed to load KPIs"); });
    return () => { on = false; };
  }, []);

  if (err) return null;
  if (!data) return (
    <div className="p-4 text-sm text-muted-foreground flex items-center gap-2 border border-border rounded-xl bg-white" data-testid="safety-dashboard-loading">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading safety KPIs…
    </div>
  );

  const k = data.kpis;
  const now = new Date();
  const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
  const drill = (partial, label) => onDrill?.(partial, label);

  const cards = [
    { icon: ShieldAlert, label: "Incidents This Month", k: { ...k.incidents_this_month, testid: "incidents-month" }, display: k.incidents_this_month.value, accent: "text-[#0A0A0A]", goodWhenDown: true, onClick: () => drill({ date_from: monthStart }, "Incidents this month") },
    { icon: AlertTriangle, label: "Assistance Requests", k: { ...k.assistance_requests, testid: "assistance" }, display: k.assistance_requests.value, accent: "text-[#B45309]", goodWhenDown: true, onClick: () => drill({ date_from: monthStart, level: "assistance" }, "Assistance requests (this month)") },
    { icon: Siren, label: "Silent Emergencies", k: { ...k.silent_emergency, testid: "emergency" }, display: k.silent_emergency.value, accent: "text-[#DC2626]", goodWhenDown: true, onClick: () => drill({ date_from: monthStart, level: "emergency" }, "Silent emergencies (this month)") },
    { icon: Activity, label: "Open Incidents", k: { ...k.open_incidents, testid: "open" }, display: k.open_incidents.value, accent: "text-[#DC2626]", onClick: () => drill({ status: "active" }, "Open incidents") },
    { icon: CheckCircle2, label: "Resolved Incidents", k: { ...k.resolved_incidents, testid: "resolved" }, display: k.resolved_incidents.value, accent: "text-[#059669]", goodWhenDown: false, onClick: () => drill({ date_from: monthStart, status: "resolved" }, "Resolved incidents (this month)") },
    { icon: Timer, label: "Avg Resolution Time", k: { ...k.avg_resolution_secs, testid: "avg-resolution" }, display: fmtDuration(k.avg_resolution_secs.value), accent: "text-[#0A0A0A]", isDuration: true, goodWhenDown: true, onClick: () => drill({ date_from: monthStart, status: "resolved" }, "Resolved incidents (this month)") },
    { icon: Gauge, label: "Fastest Resolution", k: { ...k.fastest_resolution_secs, testid: "fastest" }, display: fmtDuration(k.fastest_resolution_secs.value), accent: "text-[#059669]", onClick: () => drill({ date_from: monthStart, status: "resolved" }, "Resolved incidents (this month)") },
    { icon: Gauge, label: "Longest Resolution", k: { ...k.longest_resolution_secs, testid: "longest" }, display: fmtDuration(k.longest_resolution_secs.value), accent: "text-[#B45309]", onClick: () => drill({ date_from: monthStart, status: "resolved" }, "Resolved incidents (this month)") },
    { icon: Clock3, label: "Avg Ack. Time", k: { ...k.avg_ack_secs, testid: "avg-ack" }, display: fmtDuration(k.avg_ack_secs.value), accent: "text-[#0A0A0A]", isDuration: true, goodWhenDown: true, onClick: () => drill({ date_from: monthStart, status: "resolved" }, "Resolved incidents (this month)") },
    { icon: Repeat, label: "Repeat Drivers · 30d", k: { ...k.repeat_incidents_30d, testid: "repeat-30" }, display: k.repeat_incidents_30d.value, accent: "text-[#B45309]", onClick: () => drill({ repeat: "30" }, "Repeat drivers (last 30 days)") },
    { icon: Repeat, label: "Repeat Drivers · 90d", k: { ...k.repeat_incidents_90d, testid: "repeat-90" }, display: k.repeat_incidents_90d.value, accent: "text-[#B45309]", onClick: () => drill({ repeat: "90" }, "Repeat drivers (last 90 days)") },
  ];

  return (
    <div className="space-y-2" data-testid="safety-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-1">
        <h3 className="text-sm font-bold flex items-center gap-1.5"><Gauge className="w-4 h-4 text-[#0A0A0A]" /> Fleet Safety Performance</h3>
        <span className="text-[11px] text-muted-foreground">{data.month_label} · click any KPI to filter · trends vs previous month</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2.5">
        {cards.map((c) => <Card key={c.k.testid} {...c} />)}
      </div>
    </div>
  );
};

export default SafetyDashboard;
